import type { ColumnDef } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { humanize } from '@/modules/simulator/ui/text';
import type { DrilldownMetricKey, DrilldownRequest, TicketDrilldownRow } from '@/types/analyticsV2';

/**
 * The column that shows each ticket's own value of the metric a drill-down was opened from. An
 * average marks it on its tickets; a count marks it only on the rows it counted.
 */
const METRIC_COLUMN: Record<DrilldownMetricKey, { column: string; average?: true }> = {
  aiTicketCoverage: { column: 'handled' },
  aiAutomation: { column: 'automated' },
  avgAiResponsesPerTicket: { column: 'aiResponses', average: true },
};

export const markedTicketColumn = ({ metric, part }: DrilldownRequest): string | null => {
  const { column, average } = METRIC_COLUMN[metric];
  return average || part === 'count' ? column : null;
};

const yesNo = (value: boolean) => <span className={value ? 'text-ink' : 'text-gray-600'}>{value ? 'Yes' : 'No'}</span>;

/** A ticket's columns in a drill-down. */
export const TICKET_DRILLDOWN_COLUMNS: ColumnDef<TicketDrilldownRow>[] = [
  {
    id: 'ticket',
    header: 'Ticket',
    size: 130,
    cell: ({ row }) => (
      <div className='flex flex-col'>
        <span className='text-ink'>{row.original.displayId != null ? `#${row.original.displayId}` : '—'}</span>
        {row.original.externalId ? (
          <span className='truncate text-[12px] text-gray-700'>{row.original.externalId}</span>
        ) : null}
      </div>
    ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    size: 140,
    cell: ({ row }) => (row.original.createdAt ? format(parseISO(row.original.createdAt), 'd MMM yy, HH:mm') : '—'),
  },
  {
    id: 'classification',
    header: 'Classification',
    size: 220,
    cell: ({ row }) => {
      const { category, subcategory, intent } = row.original;
      return (
        <div className='flex flex-col'>
          <span className='truncate text-ink'>{[category ?? 'Uncategorized', subcategory].filter(Boolean).join(' › ')}</span>
          {intent ? <span className='truncate text-[12px] text-gray-700'>{humanize(intent)}</span> : null}
        </div>
      );
    },
  },
  {
    id: 'preview',
    header: 'First message',
    size: 300,
    cell: ({ row }) => <p className='line-clamp-2 whitespace-normal text-gray-800'>{row.original.preview ?? '—'}</p>,
  },
  {
    id: 'handled',
    header: 'AI handled',
    size: 100,
    cell: ({ row }) => yesNo(row.original.handled),
  },
  {
    id: 'automated',
    header: 'Automated',
    size: 100,
    cell: ({ row }) => yesNo(row.original.automated),
  },
  {
    id: 'aiResponses',
    header: 'AI responses',
    size: 110,
    cell: ({ row }) => row.original.aiResponses,
  },
];
